import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { computed, inject, PLATFORM_ID, Service, signal } from '@angular/core';

import {
  A11Y_ALL_OVERRIDDEN_PROPERTIES,
  A11Y_ALL_STATE_CLASSES,
  A11Y_PREFERENCE_DEFAULTS,
  A11Y_PREFERENCE_KEYS,
  A11yPreferenceKey,
  A11yPreferences,
  a11yEffectFor,
  createDefaultA11yPreferences,
  hasNonDefaultA11yPreferences,
} from './a11y-preferences';
import {
  A11yPreferencesStorage,
  browserA11yPreferencesStorage,
  readA11yPreferences,
  writeA11yPreferences,
} from './a11y-preferences.store';

/**
 * WARSTWA STOSUJĄCA — jedyne miejsce zamieniające model na stan dokumentu.
 *
 * Wołają ją dwie strony: widget (po każdej zmianie) i — ODWZOROWANA RĘCZNIE — skrypt inline
 * w `src/index.html`, który biegnie przed pierwszym malowaniem, czyli przed istnieniem
 * Angulara. Obie strony czytają tę samą tabelę efektów z `a11y-preferences.ts`; że naprawdę
 * robią to samo, dowodzi krok 2 bramki `verify:s22-p5`, uruchamiając skrypt inline dla każdej
 * pary (klucz, wartość) i porównując wywołane mutacje z tabelą.
 *
 * SPRZĄTANIE PRZED ZASTOSOWANIEM JEST CZĘŚCIĄ KONTRAKTU. Zastosowanie jest IDEMPOTENTNE
 * i ABSOLUTNE: najpierw znika każda właściwość i każda klasa, którą ta warstwa umie postawić,
 * potem stawiane jest to, co wynika z modelu. Bez tego przełączenie kontrastu z „maksymalny"
 * na „wysoki" zostawiłoby na korzeniu role, których nowy poziom nie nadpisuje — czyli stan,
 * którego nie da się osiągnąć żadną ścieżką przez UI i którego nikt nigdy nie testował.
 *
 * SSR: `provideClientHydration()` jest włączone (`src/app/app.config.ts`), a na serwerze nie ma
 * ani `document`, ani `localStorage`. Każde dotknięcie dokumentu stoi za `isPlatformBrowser`.
 */

/** Wycinek `HTMLElement`, którego ta warstwa używa — żeby spec podał atrapę zamiast przeglądarki. */
export interface A11yDocumentRoot {
  readonly style: Pick<CSSStyleDeclaration, 'setProperty' | 'removeProperty'>;
  readonly classList: Pick<DOMTokenList, 'add' | 'remove'>;
}

export function applyA11yPreferencesTo(root: A11yDocumentRoot, preferences: A11yPreferences): void {
  for (const property of A11Y_ALL_OVERRIDDEN_PROPERTIES) {
    root.style.removeProperty(property);
  }

  for (const stateClass of A11Y_ALL_STATE_CLASSES) {
    root.classList.remove(stateClass);
  }

  for (const key of A11Y_PREFERENCE_KEYS) {
    const effect = a11yEffectFor(key, preferences[key]);

    for (const [property, value] of Object.entries(effect.properties)) {
      root.style.setProperty(property, value);
    }

    for (const stateClass of effect.classes) {
      root.classList.add(stateClass);
    }
  }
}

/**
 * Stan preferencji dostępności dla całej aplikacji.
 *
 * Singleton, bo dokument jest jeden: dwie instancje pisałyby po tym samym korzeniu w kolejności
 * zależnej od porządku wstrzykiwania. Widget jest dziś jedynym konsumentem, ale to serwis, a nie
 * komponent, jest właścicielem stanu — komponent może zostać odmontowany, preferencja nie.
 */
@Service()
export class A11yPreferencesService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private readonly storage: A11yPreferencesStorage | null;
  private readonly state = signal<A11yPreferences>(createDefaultA11yPreferences());

  readonly preferences = this.state.asReadonly();
  readonly hasChanges = computed(() => hasNonDefaultA11yPreferences(this.state()));

  constructor() {
    const browser = isPlatformBrowser(this.platformId);

    this.storage = browser ? browserA11yPreferencesStorage() : null;

    if (!browser) {
      return;
    }

    // Skrypt inline z `src/index.html` zastosował to samo przed pierwszym malowaniem. Ten odczyt
    // NIE jest jego duplikatem: skrypt nie zostawia po sobie stanu w JS, a serwis musi wiedzieć,
    // co jest ustawione, żeby widget pokazał zaznaczone opcje. Ponowne zastosowanie jest
    // idempotentne i kosztuje tyle, co jedno przejście po tabeli.
    this.state.set(readA11yPreferences(this.storage));
    this.applyToDocument();
  }

  set<K extends A11yPreferenceKey>(key: K, value: A11yPreferences[K]): void {
    this.state.update((current) => ({ ...current, [key]: value }));
    this.persistAndApply();
  }

  reset(key: A11yPreferenceKey): void {
    this.set(key, A11Y_PREFERENCE_DEFAULTS[key]);
  }

  resetAll(): void {
    this.state.set(createDefaultA11yPreferences());
    this.persistAndApply();
  }

  private persistAndApply(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    writeA11yPreferences(this.storage, this.state());
    this.applyToDocument();
  }

  private applyToDocument(): void {
    const root = this.document.documentElement as A11yDocumentRoot | null;

    if (root === null) {
      return;
    }

    applyA11yPreferencesTo(root, this.state());
  }
}
